function getConfig(name, defaultValue = null) {
  // If inside a docker container, use window.ENV
  if (window.ENV !== undefined) {
    return window.ENV[name] || defaultValue;
  }

  return import.meta.env[name] || defaultValue;
}

export function getBackendUrl() {
  const configurado = getConfig("VITE_BACKEND_URL");

  try {
    if (configurado) {
      const urlLocal = new URL(configurado, window.location.origin);
      const confHost = urlLocal.hostname;
      const confEhLocal =
        confHost === "localhost" ||
        confHost === "127.0.0.1" ||
        confHost === "0.0.0.0";

      if (confEhLocal) {
        return window.location.origin + "/";
      }
    }
  } catch {
    /* no-op */
  }

  return configurado;
}

// Monta a URL de um arquivo servido pelo backend.
//
// ### BUG corrigido (imagens não apareciam no widget chat interno / página interna)
// A configuração de proxy do Vite SÓ ENCAMINHA GETs para o backend quando:
//   method !== GET  OR  Accept: application/json  OR  Authorization presente.
// Quando o navegador carrega uma imagem ele faz GET com `Accept: image/*` e sem
// Authorization → Vite respondia 404 e as imagens nunca apareciam.
//
// A correção é **nunca devolver pathnames relativos**: sempre devolver uma URL
// absoluta que aponte DIRETO para a origem do backend, sem passar pelo proxy
// do Vite. Em localhost dev isso aponta para porta 8080 do backend direto;
// em túnel / produção usa a host configurado em VITE_BACKEND_URL.
export function getPublicFileUrl(nomeDoArquivo) {
  if (!nomeDoArquivo) return "";
  const s = String(nomeDoArquivo);

  // Helper compartilhado: monta a origem absoluta do backend sempre atual,
  // usando VITE_BACKEND_URL quando definido ou window.location + porta 8080
  // quando estamos em desenvolvimento local (hostname = 127.0.0.1/localhost).
  const montarOrigemBackend = () => {
    const configuradoRaw = String(getConfig("VITE_BACKEND_URL") || "").replace(/\/+$/, "");
    if (typeof window === "undefined") return configuradoRaw;
    const wl = window.location;

    // Mesma lógica de getBackendUrl(): se VITE_BACKEND_URL aponta para um host
    // LOCAL (127.0.0.1 / localhost / 0.0.0.0), usamos a origem REAL da janela
    // (window.location.origin). Isso garante funcionamento em túneis tipo
    // chat.civitas.com.br onde VITE_BACKEND_URL=http://127.0.0.1:8080/ é
    // inacessível do navegador cliente remoto.
    if (configuradoRaw) {
      try {
        const urlConf = new URL(configuradoRaw, wl.origin);
        const hostConf = urlConf.hostname;
        const confEhLocal =
          hostConf === "localhost" ||
          hostConf === "127.0.0.1" ||
          hostConf === "0.0.0.0";
        if (!confEhLocal) {
          // Host externo real (ex: https://api.empresa.com) → usar valor.
          return configuradoRaw;
        }
        // Host configurado é local → confiar na origem real da janela.
        // Quando acessamos via localhost:3000 dev, porta do backend é 8080.
        // Quando acessamos via túnel (chat.civitas.com.br), o proxy do túnel
        // já roteia /public/ para o backend na porta correta → sem porta extra.
        const wlEhLocal =
          wl.hostname === "localhost" ||
          wl.hostname === "127.0.0.1" ||
          wl.hostname === "0.0.0.0" ||
          wl.hostname === "";
        const portaBackend = wlEhLocal ? "8080" : wl.port || "";
        return `${wl.protocol}//${wl.hostname}${portaBackend ? `:${portaBackend}` : ""}`;
      } catch {
        /* no-op: URL mal formada cai no fallback abaixo */
      }
    }

    // Fallback: sem VITE_BACKEND_URL configurado.
    const ehLocal =
      wl.hostname === "localhost" ||
      wl.hostname === "127.0.0.1" ||
      wl.hostname === "0.0.0.0" ||
      wl.hostname === "";
    const portaBackend = ehLocal ? "8080" : wl.port || "";
    return `${wl.protocol}//${wl.hostname}${portaBackend ? `:${portaBackend}` : ""}`;
  };

  // Helper: dado um caminho (UUID, public/uuid.jpeg, /public/uuid.jpeg),
  // retorna a URL absoluta completa para o arquivo.
  const montarArquivoPublico = (caminho) => {
    const origem = montarOrigemBackend();
    const arquivo = String(caminho).replace(/^\/+/, "");
    if (/^public\//i.test(arquivo)) {
      return `${origem}/${arquivo}`;
    }
    return `${origem}/public/${arquivo}`;
  };

  // 1) URL absoluta:
  //    - Se o pathname começa com "/public/" → é arquivo do nosso backend,
  //      descartar hostname antigo (pode ser localhost/túnel antigo/porta errada)
  //      e remontar do zero com a origem da sessão atual.
  //    - Qualquer outra URL → S3 / Cloudflare / externo → manter inalterado.
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      if (/^\/public\//i.test(u.pathname) || /^\/?public\//i.test(u.pathname)) {
        return montarArquivoPublico(u.pathname + u.search + u.hash);
      }
    } catch {
      /* no-op */
    }
    return s;
  }

  // 2) Caminho relativo (UUID, "public/uuid.jpeg", "/public/uuid.jpeg")
  return montarArquivoPublico(s);
}

// Helper: retorna URL do PNG/JPEG thumbnail da 1ª página do PDF.
//   Rota backend GET /api/pdf-thumbnail/:filename?w=NNN (150-1400).
//
//   NOTA: a rota fica em /api e NÃO em /public, porque em produção (túnel/Nginx)
//   o /public é servido direto do disco como estática e a requisição nunca
//   chega no Node — logo, rotas dinâmicas ali dentro davam 404.
//
//   Usa o MESMO helper de origem (montarOrigemBackend), então funciona tanto
//   em localhost:8080 quanto em túnel chat.civitas.com.br.
export function getPdfThumbnailUrl(nomeDoArquivo, width = 600) {
  if (!nomeDoArquivo) return "";
  const s = String(nomeDoArquivo);
  let nomeArquivoPuro;
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const pathname = u.pathname.replace(/^\/+/, "");
      const match = pathname.match(/([^/]+\.pdf)$/i);
      nomeArquivoPuro = match ? match[1] : pathname.split("/").pop() || "";
    } catch {
      nomeArquivoPuro = s.split("/").pop() || "";
    }
  } else {
    nomeArquivoPuro = String(s).split("/").pop() || s;
  }
  if (!nomeArquivoPuro) return "";
  const encoded = encodeURIComponent(nomeArquivoPuro);
  const wVal = Number.isFinite(Number(width)) ? Math.max(150, Math.min(1400, Number(width))) : 600;
  // Usar a mesma origem + prefixo /api (fora do static public):
  const origemTmp = getPublicFileUrl("placeholder-to-extract-origin-only.doNotDelete") || "";
  let origem = "";
  try {
    if (origemTmp) {
      const u = new URL(origemTmp);
      origem = `${u.protocol}//${u.host}`;
    }
  } catch (_) {}
  if (!origem && typeof window !== "undefined") {
    origem = window.location.origin;
  }
  return `${origem}/api/pdf-thumbnail/${encoded}?w=${wVal}`;
}

// Id do app da Meta usado no login de Facebook e Instagram na tela de
// Conexões. Vazio esconde os botões — é o que mantém a tela utilizável em
// quem não configurou o app.
export function getFacebookAppId() {
  return getConfig("VITE_FACEBOOK_APP_ID");
}

// Configuração do Login for Business, por canal.
//
// Apps criados como "Business" não aceitam permissão solta em `scope`: elas
// ficam numa configuração criada no painel, e o login referencia o id dela.
// Pedir por scope nesses apps devolve "Invalid Scopes". Vazio = o app é do
// login clássico e o botão continua pedindo por scope.
export function getFacebookConfigId(canal) {
  return getConfig(`VITE_FACEBOOK_CONFIG_ID_${canal.toUpperCase()}`);
}

export function getHoursCloseTicketsAuto() {
  return getConfig("VITE_HOURS_CLOSE_TICKETS_AUTO");
}
